import type { ReactNode } from "react";
import { CalendarClock, Loader2, Zap } from "lucide-react";
import { formatCop } from "@/lib/credits";
import { formatCount } from "@/lib/conversation-count";
import { formatBogotaLongDate } from "@/lib/subscription-end";
import type { CardChangeKind, CardChangeView, PlanChangeOptions } from "@/lib/plan-change";
import { cn } from "@/lib/utils";

export interface CardPlanChangeProps {
  view: CardChangeView;
  options: PlanChangeOptions | null | undefined;
  kind: CardChangeKind;
  onKindChange: (kind: CardChangeKind) => void;
}

const longDate = (value: string | null) => (value ? formatBogotaLongDate(value) : "tu próxima fecha de cobro");

export function cardChangeTitle(view: CardChangeView, planName: string | undefined): string {
  return view === "upgrade" ? `Mejorar a ${planName ?? ""}`.trim() : `Cambiar a ${planName ?? ""}`.trim();
}

export function cardChangeConfirmLabel(view: CardChangeView, kind: CardChangeKind, options: PlanChangeOptions | null | undefined): string {
  if (view === "upgrade" && kind === "immediate") return `Pagar ${formatCop(options?.difference ?? 0)}`;
  return "Programar cambio";
}

function Option({ selected, onClick, children, testId }: { selected: boolean; onClick: () => void; children: ReactNode; testId: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      data-testid={testId}
      className={cn(
        "w-full rounded-xl border-2 p-3 text-left text-sm transition-colors",
        selected ? "border-primary bg-primary/5" : "border-border hover:border-primary/40",
      )}
    >
      {children}
    </button>
  );
}

export function CardPlanChange({ view, options, kind, onKindChange }: CardPlanChangeProps) {
  if (view === "loading") {
    return (
      <div className="flex items-center justify-center py-6 text-sm text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" /> Calculando el cambio…
      </div>
    );
  }

  if (view === "refused" || !options) {
    return (
      <p role="status" className="rounded-xl border border-border bg-muted/40 p-3 text-sm text-muted-foreground" data-testid="card-change-refused">
        {options?.message ?? "No pudimos calcular el cambio de plan. Intenta de nuevo en unos minutos."}
      </p>
    );
  }

  const nextDate = longDate(options.nextChargeDate);
  const newMonthly = `${formatCop(options.toPrice)} al mes`;

  if (view === "next_cycle") {
    return (
      <div className="space-y-2 rounded-xl border border-border bg-muted/30 p-4 text-sm" data-testid="card-change-next-cycle">
        <p className="flex items-start gap-2">
          <CalendarClock className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
          <span>
            El cambio a <strong>{options.toPlanName}</strong> se aplica al inicio de tu próximo ciclo, el <strong>{nextDate}</strong>.
            Hasta entonces sigues con {options.fromPlanName} y su cupo.
          </span>
        </p>
        <p>Desde el {nextDate} tu débito automático será de <strong>{newMonthly}</strong>, con la misma tarjeta.</p>
        {options.direction === "downgrade" && (
          <p className="text-xs text-muted-foreground">No hay reembolso por el ciclo en curso.</p>
        )}
      </div>
    );
  }

  const extra = options.extraConversations;
  return (
    <div className="space-y-3" data-testid="card-change-upgrade">
      <div className="space-y-2 rounded-xl border border-border bg-muted/30 p-4 text-sm">
        <div className="flex items-center justify-between font-semibold">
          <span>Pagas hoy la diferencia</span>
          <span className="tabular-nums" data-testid="card-change-difference">{formatCop(options.difference)} COP</span>
        </div>
        <p className="text-xs text-muted-foreground">
          Es la diferencia entre {formatCop(options.fromPrice)} de {options.fromPlanName} y {formatCop(options.toPrice)} de {options.toPlanName}. Pago único con tarjeta.
        </p>
        <div className="h-px bg-border" />
        <p className="flex items-start gap-2">
          <Zap className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
          <span>
            Recibes ahora el cupo de {options.toPlanName}
            {options.toConversations !== null ? `: ${formatCount(options.toConversations)} conversaciones en este ciclo` : ""}
            {extra !== null && extra > 0 ? ` (${formatCount(extra)} más que hoy)` : ""}. Tu fecha de corte no cambia.
          </span>
        </p>
        <p>
          Desde el <strong>{nextDate}</strong> tu débito automático será de <strong>{newMonthly}</strong>, con la misma tarjeta. No tienes que autorizar nada nuevo.
        </p>
      </div>

      {options.nextCycleAvailable && (
        <div className="space-y-2" role="group" aria-label="Cuándo aplicar el cambio">
          <Option selected={kind === "immediate"} onClick={() => onKindChange("immediate")} testId="card-change-kind-immediate">
            <span className="font-semibold">Mejorar ahora</span>
            <span className="block text-xs text-muted-foreground">Pagas {formatCop(options.difference)} hoy y el nuevo cupo queda activo de inmediato.</span>
          </Option>
          <Option selected={kind === "next_cycle"} onClick={() => onKindChange("next_cycle")} testId="card-change-kind-next-cycle">
            <span className="font-semibold">Aplicar en el próximo ciclo</span>
            <span className="block text-xs text-muted-foreground">
              Te quedan {options.daysLeft} {options.daysLeft === 1 ? "día" : "días"} de ciclo. Sin pagar nada hoy: el cambio empieza el {nextDate}.
            </span>
          </Option>
        </div>
      )}
    </div>
  );
}
